import type * as Sentry from "@sentry/core";
import type { App } from "vue";

import { mergeDeepRight } from "ramda";

import { getEnv } from "./solvariEnv";

type AddDataToProject = () => Pick<Sentry.Event, "contexts" | "tags">;

type CaptureArgs = Parameters<typeof Sentry.captureException>;
type CaptureException = (...args: CaptureArgs) => void;

type GlobalSentry = Partial<{
  captureException: CaptureException;
  captureLog: CaptureException;
  trackVueErrors: (app: App) => void;
}>;
type SentryInstance = Required<GlobalSentry>;

declare global {
  var sentry: GlobalSentry | undefined;

  var sentryProjects: Record<string, AddDataToProject> | undefined;
}

type BufferedCapture = {
  args: [CaptureArgs[0], CaptureArgs[1]];
  kind: "captureException" | "captureLog";
};

// Bounded so an error loop before init cannot grow without limit.
const captureBufferLimit = 25;
const captureBuffer: BufferedCapture[] = [];

/**
 * Sentry stamps an event when it is captured, which for a buffered one is flush time.
 * Record the real moment as `extra` — only when the caller's context is a plain object,
 * since a Scope instance or a scope callback must not be spread.
 */
function withOccurredAt(context: CaptureArgs[1]): CaptureArgs[1] {
  const occurredAt = new Date().toISOString();
  if (context === undefined) {
    return { extra: { occurredAt } };
  }
  if (typeof context !== "object" || context.constructor !== Object) {
    return context;
  }
  return mergeDeepRight(context, { extra: { occurredAt } });
}

function bufferCapture(
  kind: BufferedCapture["kind"],
  error: CaptureArgs[0],
  context: CaptureArgs[1],
) {
  if (captureBuffer.length >= captureBufferLimit) {
    return;
  }
  captureBuffer.push({ kind, args: [error, withOccurredAt(context)] });
}

function captureGlobalError(event: ErrorEvent) {
  getSentry().captureException(event.error ?? event.message);
}
function captureUnhandledRejection(event: PromiseRejectionEvent) {
  getSentry().captureException(event.reason);
}
let preInitHandlersInstalled = false;

/**
 * Stands in for Sentry's `globalHandlers` integration until the SDK lands. Vue's
 * `errorHandler` never sees these: rejected promises, and throws from timers, rAF and
 * event listeners. Breadcrumbs are deliberately not replicated — rebuilding them costs
 * most of what deferring the SDK saves.
 */
function startPreInitErrorHandlers() {
  if (preInitHandlersInstalled || typeof window === "undefined") {
    return;
  }
  preInitHandlersInstalled = true;
  window.addEventListener("error", captureGlobalError);
  window.addEventListener("unhandledrejection", captureUnhandledRejection);
}

/** Idempotent. Leaving these installed would double-report every error Sentry also catches. */
function stopPreInitErrorHandlers() {
  if (!preInitHandlersInstalled) {
    return;
  }
  preInitHandlersInstalled = false;
  window.removeEventListener("error", captureGlobalError);
  window.removeEventListener("unhandledrejection", captureUnhandledRejection);
}

/** Call once, directly after assigning globalThis.sentry. */
function flushSentryBuffer() {
  stopPreInitErrorHandlers();
  captureBuffer
    .splice(0)
    .forEach(({ kind, args }) => globalThis.sentry?.[kind]?.(...args));
}

/** Global Sentry facade — merges optional context, buffers before init, logs in non-prod. */
function getSentry(instanceContext?: CaptureArgs[1]): SentryInstance {
  type Context = Exclude<CaptureArgs[1], undefined>;
  function mergeContext(captureContext?: Context): Context | undefined {
    if (!instanceContext || !captureContext) {
      return instanceContext ?? captureContext;
    }
    return mergeDeepRight(
      instanceContext as Record<string, unknown>,
      captureContext as Record<string, unknown>,
    );
  }

  return {
    captureException: (error: CaptureArgs[0], hint?: CaptureArgs[1]) => {
      const context = mergeContext(hint);
      if (globalThis.sentry?.captureException) {
        globalThis.sentry.captureException(error, context);
      } else {
        bufferCapture("captureException", error, context);
      }
      if (!globalThis.sentry || getEnv().config.envType !== "production") {
        console.error(error, context);
      }
    },
    captureLog: (error: CaptureArgs[0], hint?: CaptureArgs[1]) => {
      const context = mergeContext(hint);
      if (globalThis.sentry?.captureLog) {
        globalThis.sentry.captureLog(error, context);
      } else {
        bufferCapture("captureLog", error, context);
      }
      if (!globalThis.sentry || getEnv().config.envType !== "production") {
        console.warn(error, context);
      }
    },
    trackVueErrors: globalThis.sentry?.trackVueErrors ?? trackVueErrorsFallback,
  };
}

/** Registers per-project context/tags attached to every Sentry capture. */
function addDataToSentryProject(
  project:
    | "argus-fe"
    | "company-registration-form"
    | "contact-form"
    | "lead-forms"
    | "mkt-ab-testing",
  addData: AddDataToProject,
) {
  globalThis.sentryProjects = globalThis.sentryProjects ?? {};
  globalThis.sentryProjects[project] = addData;
}

function trackVueErrorsFallback(vueApp: App) {
  vueApp.config.errorHandler = (error) => getSentry().captureException(error);
}

export {
  addDataToSentryProject,
  flushSentryBuffer,
  getSentry,
  startPreInitErrorHandlers,
  stopPreInitErrorHandlers,
};
export type { AddDataToProject, CaptureException, SentryInstance };
